<script setup lang="ts">
  // Status multi-filter for the unified orders history: checkbox rows for
  // all order statuses (empty selection = all), pending state applied via
  // Aplicar — same interaction as the price popover. Hidden on the Mesas tab.
  import { computed, ref } from 'vue';
  import { OrderStatus } from '@/enums/OrderEnums';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';

  const props = defineProps<{
    /** Applied statuses (empty = not narrowed). */
    statuses: OrderStatus[];
  }>();

  const emit = defineEmits<{
    (e: 'apply', value: OrderStatus[]): void;
  }>();

  // Reuses the legacy `order.status.*` keys (keyed by the raw enum value).
  const statusOptions = Object.values(OrderStatus);

  const isNarrowed = computed(() => props.statuses.length > 0);

  //#region Pending selection (applied on "Aplicar")

  const pending = ref<OrderStatus[]>([]);

  function syncPendingFromApplied(): void {
    pending.value = [...props.statuses];
  }

  function handleToggle(open: boolean, toggle: () => void): void {
    if (!open) syncPendingFromApplied();
    toggle();
  }

  function toggleStatus(status: OrderStatus): void {
    pending.value = pending.value.includes(status)
      ? pending.value.filter((s) => s !== status)
      : [...pending.value, status];
  }

  function apply(close: () => void): void {
    emit('apply', [...pending.value]);
    close();
  }

  function reset(close: () => void): void {
    pending.value = [];
    emit('apply', []);
    close();
  }

  //#endregion
</script>

<template>
  <PosUiPopover placement="bottom" align="start">
    <template #trigger="{ open, toggle }">
      <PosUiButton
        color="neutral"
        variant="outline"
        size="md"
        icon="list-status"
        class="status-trigger"
        @click="handleToggle(open, toggle)"
      >
        <span class="status-trigger-label">
          {{ $t('posV2.historyStatusLabel') }}
        </span>
        <span v-if="isNarrowed" class="status-active-dot" />
      </PosUiButton>
    </template>

    <template #default="{ close }">
      <div class="status-panel">
        <div class="status-options">
          <button
            v-for="status in statusOptions"
            :key="status"
            type="button"
            class="status-option"
            :class="{ checked: pending.includes(status) }"
            @click="toggleStatus(status)"
          >
            <PosUiIcon
              :name="
                pending.includes(status)
                  ? 'checkbox-marked'
                  : 'checkbox-blank-outline'
              "
              class="option-checkbox"
            />
            <span class="option-label">{{ $t(`order.status.${status}`) }}</span>
          </button>
        </div>

        <div class="status-actions">
          <PosUiButton
            color="neutral"
            variant="ghost"
            size="sm"
            @click="reset(close)"
          >
            {{ $t('posV2.historyPriceReset') }}
          </PosUiButton>
          <PosUiButton
            color="primary"
            variant="solid"
            size="sm"
            @click="apply(close)"
          >
            {{ $t('posV2.historyPriceApply') }}
          </PosUiButton>
        </div>
      </div>
    </template>
  </PosUiPopover>
</template>

<style scoped lang="scss">
  .status-trigger {
    @apply tw-relative;

    .status-trigger-label {
      @apply tw-hidden md:tw-inline;
    }

    .status-active-dot {
      @apply tw-absolute tw-right-1 tw-top-1 tw-h-2 tw-w-2 tw-rounded-full tw-bg-orange-600;
    }
  }

  .status-panel {
    @apply tw-flex tw-flex-col tw-gap-3;

    .status-options {
      @apply tw-flex tw-flex-col;

      .status-option {
        @apply tw-flex tw-min-h-[40px] tw-cursor-pointer tw-select-none tw-items-center tw-gap-2 tw-rounded-md tw-px-2 tw-text-left tw-transition-colors;

        &:hover {
          @apply tw-bg-neutral-50;
        }

        .option-checkbox {
          @apply tw-flex-none tw-text-lg tw-text-neutral-400;
        }

        &.checked .option-checkbox {
          @apply tw-text-orange-600;
        }

        .option-label {
          @apply tw-text-sm tw-text-neutral-700;
        }
      }
    }

    // Sticky within the scrolling popover panel: the options list can
    // overflow the panel's max-height, but Aplicar must always be visible.
    // Negative margins/offset bleed over the panel's p-3 (sticky offsets
    // respect the scroller's padding) so the bar spans edge to edge and its
    // white bg covers rows scrolling underneath.
    .status-actions {
      @apply tw-sticky tw--bottom-3 tw-z-10 tw--mx-3 tw--mb-3 tw-flex tw-items-center tw-justify-end tw-gap-2 tw-border-t tw-border-neutral-100 tw-bg-white tw-px-3 tw-py-2 tw-shadow-[0_-4px_6px_-4px_rgba(15,23,42,0.12)];
    }
  }
</style>
